import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HomeIcon } from "../Icons/Miscellaneous/HomeIcon";
import { CursoIcon } from "../Icons/Messaging/CursoIcon";
import { DiplomadoIcon } from "../Icons/Messaging/DiplomadoIcon";
import { LogoutIcon } from "../Icons/Miscellaneous/LogoutIcon";
import { storageService } from "../../Services/storage_service";
import { Button } from "../Button/Button";
import { useLogout } from "../../Hooks/useLogout";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { RUTAS } from "rutas";
import { useQuery } from "Hooks/useQuery";
import { getUsuarioPerfil } from "Services/usuario_service";
import { ScriptIcon } from "Components/Icons/Documents/ScriptIcon";
import "./Menu.scss";

/**
 * Componente de menú que muestra opciones de navegación e información del usuario.
 * Puede ser utilizado dentro de drawers, modales u otros contenedores.
 * @param {string} [className=""] - Clases CSS adicionales para el componente
 * @param {Function} [onClose] - Función callback que se ejecuta al cerrar el menú
 * @param {...Object} props - Props adicionales que se pasan al contenedor del menú
 * @returns {JSX.Element} Componente Menu renderizado
 */
export const Menu = ({ className = "", onClose, ...props }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { authUser } = useSelector((state) => state.auth);
  const { logout } = useLogout();
  const { data: perfilData } = useQuery(
    getUsuarioPerfil,
    { idUsuario: authUser.idUsuario },
    Boolean(authUser.idUsuario)
  );

  const handleMenuItemClick = (path) => {
    navigate(path);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleLogoutClick = () => {
    logout();
    window.location.reload();
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    onClose?.();
    setOpen(false);
  };

  return (
    <>
      <div className={`menu ${className}`} {...props}>
        <h2 className="menu__title">Menú</h2>
        <div className="menu__divider" />
        <nav className="menu__nav">
          <button
            className="menu__item"
            onClick={() => handleMenuItemClick(RUTAS.PRINCIPAL)}
          >
            <HomeIcon />
            <span className="menu__item-text">Inicio</span>
          </button>

          <button
            className="menu__item"
            onClick={() => handleMenuItemClick(RUTAS.MIS_CURSOS)}
          >
            <CursoIcon />
            <span className="menu__item-text">Mis cursos</span>
          </button>

          <button
            className="menu__item"
            onClick={() => handleMenuItemClick(RUTAS.MIS_PAQUETES)}
          >
            <DiplomadoIcon />
            <span className="menu__item-text">Mis paquetes de cursos</span>
          </button>

          <button
            className="menu__item"
            onClick={() =>
              handleMenuItemClick(RUTAS.MIS_CAPACITACIONES_PRESENCIALES)
            }
          >
            <ScriptIcon />
            <span className="menu__item-text">Capacitaciones Presenciales</span>
          </button>
        </nav>

        <div className="menu__user-section">
          <h3 className="menu__title">Usuario</h3>
          <div className="menu__divider" />
          <div
            className="menu__user-info"
            onClick={() => handleMenuItemClick(RUTAS.MI_USUARIO)}
          >
            <div className="menu__user-avatar">
              {perfilData?.foto ? (
                <img
                  src={perfilData.foto}
                  alt="User avatar"
                  className="menu__user-avatar-image"
                />
              ) : (
                <div className="menu__user-avatar-placeholder">
                  {perfilData?.nombreCompleto?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
            </div>
            <span className="menu__user-name">
              {perfilData?.nombreCompleto}
            </span>
          </div>
          <button className="menu__logout" onClick={handleClickOpen}>
            <LogoutIcon className="menu__logout-icon" />
            <span className="menu__logout-text">Cerrar sesión</span>
          </button>
        </div>
      </div>
      <Dialog
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        className="alert-logout-dialog"
      >
        <DialogTitle className="alert-logout-dialog__title">
          ¿Cerrar sesión?
        </DialogTitle>
        <DialogContent>
          <p className="alert-logout-dialog__description">
            ¿Estás seguro de que deseas cerrar sesión? Esta acción finalizará tu
            sesión actual.
          </p>
        </DialogContent>
        <DialogActions>
          <div className="alert-logout-dialog__actions">
            <Button onClick={handleClose} size="medium" variant="secundario">
              Cancelar
            </Button>
            <Button
              onClick={handleLogoutClick}
              autoFocus
              variant="danger"
              size="medium"
            >
              Aceptar
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};
